import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { FileInput } from "components/FileInput/FileInput";
import { ArrowRight } from "components/icons/ArrowRight";
import { Form, Formik, FormikValues, useFormikContext } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { useGetCurrenciesQuery } from "store/apis/invoices";
import { useUploadCSVTransactionMutation } from "store/apis/transactions";
import { setManualTransactionAdditionInvoice } from "store/slices/linkInkleInvoice";
import { Transaction } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { object, string } from "yup";
import { AddTransactionModalBody } from "./AddTransactionModalBody";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";

export const ManualTransactionAddStepper = ({
  isCSV,
  currentStep,
  onStepperClick,
}: {
  isCSV: boolean;
  onStepperClick: (step: number) => void;
  currentStep: number;
}) => {
  const { isMobile } = useWindowSize();
  const breadcrumbs = isCSV
    ? [
        { name: "Select bank", step: 1 },
        { name: "Map columns", step: 2 },
        { name: "Review transactions", step: 3 },
      ]
    : [
        { name: "Select bank", step: 1 },
        { name: "Add transactions", step: 2 },
      ];

  return (
    <Stepper size={isMobile ? "xs" : "small"} direction="horizontal">
      {breadcrumbs?.map(({ name, step }) => (
        <Stepper.Step
          step={step}
          key={step}
          isActive={currentStep >= step}
          clickable={currentStep >= step}
          onClick={() => onStepperClick(step)}
        >
          <div className="t-flex t-items-center">
            {name}
            {step !== breadcrumbs.length && <ArrowRight color="currentColor" />}
          </div>
        </Stepper.Step>
      ))}
    </Stepper>
  );
};

const BankSelector = () => {
  const {
    values: { account },
    setFieldValue,
  } = useFormikContext<{ account: string | null; currency: string }>();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { data: banks } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const bankLabel = (
    logo: string,
    name: string,
    nickname: string,
    mask: string
  ) => (
    <div className="t-flex t-items-center t-gap-2">
      <Avatar src={logo} alt={name} size="extra-small" />
      <span>{`${nickname}${mask ? `  ••••${mask}` : ""}`}</span>
    </div>
  );

  const banksAccounts = banks?.accounts.map(
    ({ account: { mask, nickname, uuid }, bank_brand: { logo, name } }) => ({
      value: uuid,
      label: bankLabel(logo, name, nickname, mask),
      data: `${nickname}${mask ? ` ${mask}` : ""} ${name}`,
    })
  );

  const selectedBank = banks?.accounts?.find(
    ({ account: { uuid } }) => account === uuid
  );

  const selectedBankLabel = selectedBank
    ? bankLabel(
        selectedBank.bank_brand.logo,
        selectedBank.bank_brand.name,
        selectedBank.account.nickname,
        selectedBank.account.mask
      )
    : null;

  const handleBankChange = (
    option: OptionData | readonly OptionData[] | null
  ) => {
    if (option instanceof Array || !option) return;
    const selected = banks?.accounts?.find(
      ({ account: { uuid } }) => uuid === option.value
    );

    setFieldValue("account", option.value);
    setFieldValue("currency", selected?.account?.currency || "USD");
  };

  return (
    <Combobox
      options={banksAccounts}
      label="Select account"
      required
      name="account"
      withForm
      placeholder="Select or input account"
      menuPortalTarget={document.getElementById("root")}
      block
      onChange={handleBankChange}
      filterOption={(option, inputValue) =>
        (option.data.data || "")
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      }
      {...{
        value:
          account && selectedBankLabel
            ? {
                label: selectedBankLabel,
                value: account,
              }
            : null,
      }}
    />
  );
};

const CurrencySelector = () => {
  const {
    values: { account, currency },
  } = useFormikContext<{ account: string | null; currency: string }>();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { data: currenciesResponse } = useGetCurrenciesQuery();
  const { data: banks } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const currencyOptions = useMemo(() => {
    if (!currenciesResponse) return [];
    return Object.entries(currenciesResponse).map(([code, name]) => ({
      label: `${code} - ${name}`,
      value: code,
    }));
  }, [currenciesResponse]);

  const selectedBank = banks?.accounts?.find(
    ({ account: { uuid } }) => uuid === account
  );
  const accountHasCurrency = Boolean(selectedBank?.account?.currency);

  return (
    <>
      <Combobox
        withForm
        name="currency"
        label="Select account currency"
        required
        options={currencyOptions}
        isDisabled={accountHasCurrency}
        placeholder="Select a currency"
        menuPortalTarget={document.body}
        block
      />
      {!accountHasCurrency && account && (
        <Banner
          type="info"
          description="The currency selected will be permanently assigned to the chosen bank account once the transactions are added. This cannot be undone."
        />
      )}
    </>
  );
};

const SelectAddAccount = ({
  csv,
  selectedAccountId,
  defaultAccountCurrency,
  onSubmit,
  onBack,
}: {
  csv?: File;
  selectedAccountId?: string;
  defaultAccountCurrency: string;
  onSubmit: (values: {
    csv: File | null;
    account: string | null;
    currency: string;
  }) => void;
  onBack?: () => void;
}) => {
  return (
    <Formik
      initialValues={{
        csv: csv || null,
        account: selectedAccountId || null,
        currency: defaultAccountCurrency,
      }}
      onSubmit={onSubmit}
      enableReinitialize
      validationSchema={object({
        account: string().required("Please select account").nullable(),
        currency: string().required("Please select currency"),
      })}
      validateOnChange
      validateOnMount
    >
      {({
        submitForm,
        setFieldValue,
        values: { csv },
        isSubmitting,
        isValid,
      }) => (
        <Form className="t-m-0 t-w-full t-flex t-flex-col t-flex-1 t-min-h-0">
          <Modal.Body className="t-flex t-gap-3 t-flex-col t-w-full t-flex-1 t-min-h-0 sm:t-px-4">
            {Boolean(csv) && (
              <FileInput
                withForm
                name="csv"
                onDelete={() => {
                  setFieldValue("csv", null);
                  setFieldValue("account", null);
                  onBack?.();
                }}
                label="Uploaded file"
                file={
                  csv && {
                    name: csv.name,
                    file_type: "CSV",
                    is_previewable: false,
                    uuid: "",
                  }
                }
              />
            )}
            <BankSelector />
            <CurrencySelector />
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={onBack} type="reset" disabled={isSubmitting}>
              Back
            </Button>
            <Button
              customType="primary"
              type="submit"
              onClick={submitForm}
              isLoading={isSubmitting}
              disabled={!isValid || isSubmitting}
            >
              Next
            </Button>
          </Modal.FooterButtonGroup>
        </Form>
      )}
    </Formik>
  );
};

type TransactionsBankSelectorProps = ModalProps & {
  onBack: () => void;
  csv?: File;
  step?: number;
  defaultTransaction?: Transaction;
};

export const TransactionsBankSelector = ({
  close,
  isOpen,
  onBack,
  csv,
  step = 1,
  defaultTransaction,
}: TransactionsBankSelectorProps) => {
  const { alertToast } = useToast();
  const dispatch = useDispatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [selectedAccountId, setSelectedAccountId] = useState(
    defaultTransaction?.from?.bank_account?.uuid || ""
  );
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [currentStep, setCurrentStep] = useState(step);

  const [uploadCSV, { data: uploadedCsvData, reset }] =
    useUploadCSVTransactionMutation();

  const { data: banks } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );
  const selectedBank = banks?.accounts?.find(
    ({ account: { uuid } }) => uuid === selectedAccountId
  );

  const onSubmit = async (value: FormikValues) => {
    try {
      setSelectedCurrency(value.currency || "USD");

      if (csv) {
        await uploadCSV({
          accountId: value.account,
          csv,
          entityId,
          groupId,
        }).unwrap();

        setSelectedAccountId(value.account);
        setCurrentStep(2);
        return;
      }
      setSelectedAccountId(value.account);
      setCurrentStep(2);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const onClose = () => {
    reset();
    close();
    setCurrentStep(1);
    setSelectedAccountId("");
    dispatch(
      setManualTransactionAdditionInvoice({
        invoiceId: "",
        invoiceDueAmount: 0,
        type: "BILL",
      })
    );
  };

  const onBackFromAddTransactionModalBody = () => {
    if (uploadedCsvData) {
      reset();
    }
    setCurrentStep((step) => step - 1);
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content
          size={
            currentStep === 3 || (!csv && currentStep === 2) ? "xxxl" : "xl"
          }
          useCustomOverlay
        >
          <Modal.Header className="sm:t-pl-4">
            <div>
              {defaultTransaction ? (
                <Modal.Title>Duplicate Transactions</Modal.Title>
              ) : (
                <Modal.Title>Manually Add Transactions</Modal.Title>
              )}
              <Modal.Subtitle>
                <ManualTransactionAddStepper
                  isCSV={Boolean(csv)}
                  currentStep={currentStep}
                  onStepperClick={setCurrentStep}
                />
              </Modal.Subtitle>
            </div>
            <Modal.Close />
          </Modal.Header>
          {currentStep === 1 ? (
            <SelectAddAccount
              csv={csv}
              onSubmit={onSubmit}
              onBack={onBack}
              selectedAccountId={selectedAccountId}
              defaultAccountCurrency={selectedBank?.account?.currency || "USD"}
            />
          ) : (
            <AddTransactionModalBody
              onBack={onBackFromAddTransactionModalBody}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              close={onClose}
              selectedAccountId={selectedAccountId}
              uploadedCsvData={uploadedCsvData}
              defaultTransaction={defaultTransaction}
              currency={selectedCurrency}
            />
          )}
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
